// FULFILMENT ON THE QUOTE SCREEN -- M4 (O8-O11, owner approvals D195).
//
//   Receive goods      GRNs on via-warehouse OEM orders (partial ordinary;
//                      short / damaged need a reason; a mistake is a reversing GRN)
//   Deliver            delivery notes (challans) from our warehouse, bounded by
//                      what was received GOOD there for each quote line
//   Customs actuals    one Bill of Entry per row, import orders only
//   Close out          Admin: close / reopen with a reason; the "Actual" report
//
// ⚠️ THE SCREEN IS NOT THE GUARD. Every bound, every refusal and who may do
// what is the server's (models/fulfilment.py); a refusal is shown as it came.
// ⚠️ NO PREFILL. Dates and quantities start EMPTY -- an absent value is never
// today or zero; "Use remaining quantity (N)" fills one only when clicked.
// ⚠️ NOTHING HERE PRICES. Quantities only, except the Admin-only "Actual"
// report, which only DISPLAYS the server's read-only figures.

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  getGrns, recordGrn, reverseGrn, getDeliveryNotes, createDeliveryNote, reverseDeliveryNote,
  getCustomsEntries, recordCustomsEntry, reverseCustomsEntry, getTrueMargin, setQuoteOrderClosed,
  getOrderStatus,
  type Grn, type DeliveryNote, type CustomsEntry, type OemOrder, type OrderStatusView, type TrueMarginView,
} from "@/services/api";
import { formatDate, formatRupees, formatTimestampDate } from "@/lib/format";
import { Pill } from "@/components/OemConfirmationsPanel";
import { DeliveryNotePreview } from "@/components/DeliveryNotePreview";
import { colors, semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";

const BOX: React.CSSProperties = {
  marginTop: spacing[12], border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  backgroundColor: semanticColors.surface,
};
const HEAD: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: spacing[10], flexWrap: "wrap",
  padding: `${spacing[10]}px ${spacing[12]}px`, borderBottom: `1px solid ${semanticColors.border}`,
};
const TITLE: React.CSSProperties = { fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary };
const NOTE: React.CSSProperties = { fontSize: typography.fontSize.sm, color: semanticColors.textSecondary };
const HELP: React.CSSProperties = { fontSize: typography.fontSize.xs, color: semanticColors.textMuted };
const TD: React.CSSProperties = { padding: `${spacing[6]}px ${spacing[8]}px`, fontSize: typography.fontSize.sm, borderBottom: `1px solid ${semanticColors.border}`, verticalAlign: "top" };
const TH: React.CSSProperties = { ...TD, textAlign: "left", color: semanticColors.textMuted, fontWeight: typography.fontWeight.medium, whiteSpace: "nowrap" };
const NUM: React.CSSProperties = { ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const BTN: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[10]}px`, backgroundColor: semanticColors.surface,
  color: semanticColors.textSecondary, border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.sm, cursor: "pointer", whiteSpace: "nowrap",
};
const PRIMARY: React.CSSProperties = { ...BTN, backgroundColor: colors.indigo600, color: semanticColors.onAccent, border: "none" };
const INPUT: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`, border: `1px solid ${semanticColors.border}`,
  borderRadius: radius.base, fontSize: typography.fontSize.sm, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, minWidth: 0,
};
const ERR: React.CSSProperties = { ...NOTE, color: colors.red600 };

const num = (raw: string): number | null => (raw.trim() === "" ? null : Number(raw));
const fmt = (n: number) => String(Math.round(n * 1000) / 1000);

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "grid", gap: spacing[2], fontSize: typography.fontSize.xs, color: semanticColors.textMuted }}>
      {label}{children}
    </label>
  );
}

function Remaining({ n, onUse, testId }: { n: number; onUse: (v: string) => void; testId: string }) {
  if (n <= 1e-9) return null;
  return (
    <button type="button" data-testid={testId} style={BTN} onClick={() => onUse(fmt(n))}>
      Use remaining quantity ({fmt(n)})
    </button>
  );
}

// ---- THE ONE READ -------------------------------------------------------------------

export interface FulfilmentData {
  grns: Grn[]; notes: DeliveryNote[]; customs: CustomsEntry[]; status: OrderStatusView | null;
  nextGrn: string | null; nextDn: string | null; hidden: boolean; loaded: boolean; error: string;
  refresh: () => void; tick: number;
}

export function useFulfilmentData(quoteId: number | undefined, reloadKey = 0, active = true): FulfilmentData {
  const [grns, setGrns] = useState<Grn[]>([]);
  const [notes, setNotes] = useState<DeliveryNote[]>([]);
  const [customs, setCustoms] = useState<CustomsEntry[]>([]);
  const [status, setStatus] = useState<OrderStatusView | null>(null);
  const [nextGrn, setNextGrn] = useState<string | null>(null);
  const [nextDn, setNextDn] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);
  const load = useCallback(async () => {
    if (quoteId === undefined || !active) return;
    const [g, d, c, s] = await Promise.all([getGrns(quoteId), getDeliveryNotes(quoteId), getCustomsEntries(quoteId),
      getOrderStatus(quoteId)]);
    if (g.notFound || d.notFound || c.notFound || s.notFound) { setHidden(true); setLoaded(true); return; }
    if (!g.ok || !d.ok || !c.ok || !s.ok) { setError(g.error || d.error || c.error || s.error || "Could not read fulfilment."); return; }
    setHidden(false); setError(""); setLoaded(true);
    setGrns(g.grns!); setNotes(d.notes!); setCustoms(c.entries!); setStatus(s.view!);
    setNextGrn(g.nextNumber ?? null); setNextDn(d.nextNumber ?? null);
  }, [quoteId, active]);
  useEffect(() => { void load(); }, [load, reloadKey, tick]);
  const refresh = useCallback(() => setTick((t) => t + 1), []);
  return { grns, notes, customs, status, nextGrn, nextDn, hidden, loaded, error, refresh, tick };
}

// ---- STEP 7: RECEIVE GOODS (GRN) -------------------------------------------------------

function GrnForm({ order, nextNumber, onDone }: { order: OemOrder; nextNumber: string | null; onDone: () => void }) {
  const [date, setDate] = useState("");
  const [docRef, setDocRef] = useState("");
  const [good, setGood] = useState<Record<number, string>>({});
  const [damaged, setDamaged] = useState<Record<number, string>>({});
  const [perDoc, setPerDoc] = useState<Record<number, string>>({});
  const [reason, setReason] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    setBusy(true); setError("");
    const lines = order.lines.filter((l) => (good[l.id] ?? "") !== "" || (damaged[l.id] ?? "") !== "").map((l) => ({
      order_line_id: l.id, qty_received: num(good[l.id] ?? ""), qty_damaged: num(damaged[l.id] ?? ""),
      qty_per_document: num(perDoc[l.id] ?? ""), discrepancy_reason: reason[l.id] || undefined,
    }));
    const res = await recordGrn(order.id, { received_date: date || null, oem_document_ref: docRef || undefined, lines });
    setBusy(false);
    if (!res.ok) { setError(res.error || "The goods receipt was not recorded."); return; }
    setGood({}); setDamaged({}); setPerDoc({}); setReason({}); setDate(""); setDocRef("");
    onDone();
  };
  return (
    <div data-testid={`grn-form-${order.id}`} style={{ padding: spacing[12], backgroundColor: semanticColors.panelBg, display: "grid", gap: spacing[8] }}>
      <div style={{ display: "flex", gap: spacing[8], flexWrap: "wrap" }}>
        <Field label="Date received"><input type="date" data-testid={`grn-date-${order.id}`} style={INPUT}
          value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label="OEM delivery note / invoice no."><input data-testid={`grn-docref-${order.id}`} style={INPUT}
          value={docRef} onChange={(e) => setDocRef(e.target.value)} /></Field>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["Part", "Ordered", "Received so far", "Good units now", "Damaged", "Per OEM document", "Reason (short or damaged)"].map((h) => <th key={h} style={TH}>{h}</th>)}</tr></thead>
          <tbody>
            {order.lines.map((l) => {
              const left = l.qty_ordered - (l.received_qty ?? 0) - (l.damaged_qty ?? 0);
              return (
                <tr key={l.id}>
                  <td style={TD}>{l.part_number}</td>
                  <td style={NUM}>{l.qty_ordered}</td>
                  <td style={NUM}>{fmt(l.received_qty ?? 0)}{(l.damaged_qty ?? 0) > 0 ? ` (+${fmt(l.damaged_qty ?? 0)} damaged)` : ""}</td>
                  <td style={TD}>
                    <div style={{ display: "flex", gap: spacing[6], flexWrap: "wrap" }}>
                      <input aria-label={`Good units received now, ${l.part_number}`} data-testid={`grn-good-${l.id}`} inputMode="decimal"
                        style={{ ...INPUT, width: 80 }} value={good[l.id] ?? ""} onChange={(e) => setGood((g) => ({ ...g, [l.id]: e.target.value }))} />
                      <Remaining n={left} testId={`grn-remaining-${l.id}`} onUse={(v) => setGood((g) => ({ ...g, [l.id]: v }))} />
                    </div>
                  </td>
                  <td style={TD}><input aria-label={`Damaged units, ${l.part_number}`} data-testid={`grn-damaged-${l.id}`} inputMode="decimal"
                    style={{ ...INPUT, width: 70 }} value={damaged[l.id] ?? ""} onChange={(e) => setDamaged((g) => ({ ...g, [l.id]: e.target.value }))} /></td>
                  <td style={TD}><input aria-label={`Quantity on the OEM document, ${l.part_number}`} data-testid={`grn-perdoc-${l.id}`} inputMode="decimal"
                    style={{ ...INPUT, width: 70 }} value={perDoc[l.id] ?? ""} onChange={(e) => setPerDoc((g) => ({ ...g, [l.id]: e.target.value }))} /></td>
                  <td style={TD}><input aria-label={`Reason, ${l.part_number}`} data-testid={`grn-reason-${l.id}`}
                    style={INPUT} value={reason[l.id] ?? ""} onChange={(e) => setReason((g) => ({ ...g, [l.id]: e.target.value }))} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {error && <div role="alert" data-testid={`grn-error-${order.id}`} style={ERR}>{error}</div>}
      <div style={{ display: "grid", justifyItems: "start", gap: spacing[2] }}>
        <button type="button" data-testid={`grn-save-${order.id}`} style={PRIMARY} disabled={busy} onClick={() => { void submit(); }}>
          {busy ? "Recording…" : "Record goods receipt"}
        </button>
        <span style={HELP}>Takes the next GRN number{nextNumber ? ` (${nextNumber})` : ""}. Permanent: a mistake is put right by a reversing GRN.</span>
      </div>
    </div>
  );
}

export function ReceiveGoodsBlock({ orders, data, canRecord }: {
  orders: OemOrder[]; data: FulfilmentData; canRecord: boolean;
}) {
  const [doc, setDoc] = useState<Grn | null>(null);
  const [error, setError] = useState("");
  const wh = orders.filter((o) => o.ship_to_kind === "warehouse" && o.status !== "cancelled");
  const reverse = async (g: Grn) => {
    const reason = window.prompt(`Why is ${g.number} reversed? (required)`);
    if (reason === null) return;
    const res = await reverseGrn(g.id, reason);
    if (!res.ok) setError(res.error || "Not reversed."); else data.refresh();
  };
  return (
    <section data-testid="receive-goods" style={BOX}>
      <div style={HEAD}>
        <span style={TITLE}>Goods received at our warehouse</span>
        <span style={NOTE}>Each receipt (GRN) is counted against its OEM order line. Partial receipts are normal; short or damaged units need a reason.</span>
      </div>
      {wh.length === 0 && <div data-testid="receive-none" style={{ ...NOTE, padding: spacing[12] }}>
        No OEM order on this quote ships to our warehouse. Drop-ship orders go straight to the customer and need no receipt.</div>}
      {wh.map((o) => (
        <div key={o.id} data-testid={`receive-order-${o.id}`} style={{ borderBottom: `1px solid ${semanticColors.border}` }}>
          <div style={{ ...NOTE, padding: `${spacing[8]}px ${spacing[12]}px`, color: semanticColors.textPrimary }}>
            <strong>{o.number}</strong> · {o.oem} · {o.status === "delivered" ? "fully received" : "waiting for goods"}
          </div>
          {canRecord && o.status !== "delivered" && <GrnForm key={`${o.id}-${data.tick}`} order={o} nextNumber={data.nextGrn} onDone={data.refresh} />}
        </div>
      ))}
      {data.grns.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["GRN", "OEM order", "Date", "Lines", "OEM document", ""].map((h) => <th key={h} style={TH}>{h}</th>)}</tr></thead>
            <tbody>
              {data.grns.map((g) => (
                <tr key={g.id} data-testid={`grn-row-${g.id}`}>
                  <td style={{ ...TD, fontWeight: typography.fontWeight.medium }}>{g.number}{g.reverses ? ` (reverses ${g.reverses})` : ""}{g.reversed_by ? ` · reversed by ${g.reversed_by}` : ""}</td>
                  <td style={TD}>{g.oem_order_number}</td>
                  <td style={TD}>{formatDate(g.received_date)}</td>
                  <td style={TD}>{g.lines.map((l) => `${l.part_number} × ${fmt(l.qty_received)}${l.qty_damaged ? ` (+${fmt(l.qty_damaged)} damaged)` : ""}${l.discrepancy_reason ? `: ${l.discrepancy_reason}` : ""}`).join("; ")}</td>
                  <td style={TD}>{g.oem_document_ref ?? "—"}</td>
                  <td style={{ ...TD, whiteSpace: "nowrap" }}>
                    <button type="button" data-testid={`grn-doc-${g.id}`} style={BTN} onClick={() => setDoc(g)}>Document</button>{" "}
                    {canRecord && !g.reverses && !g.reversed_by && <button type="button" data-testid={`grn-reverse-${g.id}`} style={BTN} onClick={() => { void reverse(g); }}>Reverse</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {error && <div role="alert" style={{ ...ERR, padding: spacing[12] }}>{error}</div>}
      {doc && <GrnDocument grn={doc} onClose={() => setDoc(null)} />}
    </section>
  );
}

/** The GRN document: INTERNAL ONLY (never sent to a customer). */
function GrnDocument({ grn, onClose }: { grn: Grn; onClose: () => void }) {
  useEffect(() => {
    document.body.classList.add("printing-document");
    return () => document.body.classList.remove("printing-document");
  }, []);
  return createPortal(
    <div data-testid="grn-doc" role="dialog" aria-modal="true" aria-label={`Goods receipt ${grn.number}`} style={{
      position: "fixed", inset: 0, backgroundColor: semanticColors.overlay, overflowY: "auto", zIndex: 1000,
      display: "flex", flexDirection: "column", alignItems: "center", padding: spacing[16],
    }}>
      <div data-testid="grn-doc-toolbar" style={{ display: "flex", gap: spacing[8], marginBottom: spacing[8] }}>
        <button type="button" style={PRIMARY} onClick={() => window.print()}>Print</button>
        <button type="button" data-testid="grn-doc-close" style={BTN} onClick={onClose}>Close</button>
      </div>
      <div data-testid="grn-doc-page" style={{ backgroundColor: semanticColors.surface, padding: spacing[24], borderRadius: radius.base, maxWidth: "100%" }}>
        <div style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.semibold }}>Goods receipt {grn.number}</div>
        <div style={NOTE}>INTERNAL -- not a customer document. OEM order {grn.oem_order_number} · {grn.warehouse ?? "—"} · received {formatDate(grn.received_date)}
          {grn.oem_document_ref ? ` · OEM document ${grn.oem_document_ref}` : ""}{grn.reverses ? ` · REVERSES ${grn.reverses}: ${grn.reverse_reason ?? ""}` : ""}</div>
        <table style={{ marginTop: spacing[12], borderCollapse: "collapse" }}>
          <thead><tr>{["#", "Part", "Good", "Damaged", "Per OEM document", "Discrepancy"].map((h) => <th key={h} style={TH}>{h}</th>)}</tr></thead>
          <tbody>{grn.lines.map((l, i) => (
            <tr key={i}><td style={TD}>{l.line_no ?? "—"}</td><td style={TD}>{l.part_number}</td><td style={NUM}>{fmt(l.qty_received)}</td>
              <td style={NUM}>{l.qty_damaged != null ? fmt(l.qty_damaged) : "—"}</td><td style={NUM}>{l.qty_per_document != null ? fmt(l.qty_per_document) : "Not stated"}</td>
              <td style={TD}>{l.discrepancy ? `${l.discrepancy.replace(/_/g, " ")}: ${l.discrepancy_reason ?? ""}` : "—"}</td></tr>))}
          </tbody>
        </table>
        <div style={{ ...HELP, marginTop: spacing[12] }}>Recorded by {grn.recorded_by ?? "—"}{grn.recorded_at ? ` on ${formatTimestampDate(grn.recorded_at)}` : ""}.</div>
      </div>
    </div>,
    document.body,
  );
}

// ---- STEP 8: DELIVER TO THE CUSTOMER (delivery note) -------------------------------------

export function DeliverBlock({ quoteId, orders, data, canRecord, closed }: {
  quoteId: number; orders: OemOrder[]; data: FulfilmentData; canRecord: boolean; closed: boolean;
}) {
  const lines = (data.status?.lines ?? []).filter((l) => l.via_warehouse);
  const warehouses = Array.from(new Map(orders.filter((o) => o.ship_to_kind === "warehouse" && o.warehouse_id)
    .map((o) => [o.warehouse_id as number, (o.ship_to ?? "").split("\n")[0] || `Warehouse ${o.warehouse_id}`])).entries());
  const [wh, setWh] = useState<string>("");
  const [date, setDate] = useState("");
  const [qty, setQty] = useState<Record<number, string>>({});
  const [extra, setExtra] = useState({ eway: "", lr: "", transporter: "", vehicle: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<number | null>(null);
  const whId = wh || (warehouses.length === 1 ? String(warehouses[0][0]) : "");
  const submit = async () => {
    setBusy(true); setError("");
    const res = await createDeliveryNote(quoteId, {
      warehouse_id: whId ? Number(whId) : null, dn_date: date || null,
      eway_bill_no: extra.eway || undefined, lr_no: extra.lr || undefined,
      transporter: extra.transporter || undefined, vehicle_no: extra.vehicle || undefined,
      lines: lines.filter((l) => (qty[l.line_id] ?? "") !== "").map((l) => ({ quote_line_id: l.line_id, qty: num(qty[l.line_id]) })),
    });
    setBusy(false);
    if (!res.ok) { setError(res.error || "The delivery note was not created."); return; }
    setQty({}); setDate(""); setExtra({ eway: "", lr: "", transporter: "", vehicle: "" });
    data.refresh();
    if (res.note) setPreview(res.note.id);
  };
  const reverse = async (n: DeliveryNote) => {
    const reason = window.prompt(`Why is ${n.number} reversed? (required)`);
    if (reason === null) return;
    const res = await reverseDeliveryNote(n.id, reason);
    if (!res.ok) setError(res.error || "Not reversed."); else data.refresh();
  };
  return (
    <section data-testid="deliver" style={BOX}>
      <div style={HEAD}>
        <span style={TITLE}>Delivery to the customer</span>
        <span style={NOTE}>A delivery note (challan) sends goods we received to the customer's site. The challan shows no price and no OEM.</span>
      </div>
      {lines.length === 0 ? (
        <div data-testid="deliver-none" style={{ ...NOTE, padding: spacing[12] }}>Nothing on this quote comes through our warehouse.</div>
      ) : (
        <div style={{ padding: spacing[12], display: "grid", gap: spacing[8] }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>{["#", "Part", "On the quote", "Received good", "Dispatched", "To dispatch", ...(canRecord && !closed ? ["Dispatch now"] : [])].map((h) => <th key={h} style={TH}>{h}</th>)}</tr></thead>
              <tbody>{lines.map((l) => (
                <tr key={l.line_id} data-testid={`dn-line-${l.line_no}`}>
                  <td style={TD}>{l.line_no}</td><td style={TD}>{l.part_number}</td><td style={NUM}>{l.qty}</td>
                  <td style={NUM}>{fmt(l.received ?? 0)}{(l.damaged ?? 0) > 0 ? ` (${fmt(l.damaged ?? 0)} damaged)` : ""}</td>
                  <td style={NUM}>{fmt(l.dispatched ?? 0)}</td><td style={NUM} data-testid={`dn-todispatch-${l.line_no}`}>{fmt(l.to_dispatch ?? 0)}</td>
                  {canRecord && !closed && (
                    <td style={TD}><div style={{ display: "flex", gap: spacing[6], flexWrap: "wrap" }}>
                      <input aria-label={`Quantity to dispatch, line ${l.line_no}`} data-testid={`dn-qty-${l.line_no}`} inputMode="decimal"
                        style={{ ...INPUT, width: 80 }} value={qty[l.line_id] ?? ""} onChange={(e) => setQty((q) => ({ ...q, [l.line_id]: e.target.value }))} />
                      <Remaining n={l.to_dispatch ?? 0} testId={`dn-remaining-${l.line_no}`} onUse={(v) => setQty((q) => ({ ...q, [l.line_id]: v }))} />
                    </div></td>
                  )}
                </tr>))}
              </tbody>
            </table>
          </div>
          {canRecord && !closed && (
            <>
              <div style={{ display: "grid", gap: spacing[8], gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
                <Field label="From warehouse">
                  <select data-testid="dn-warehouse" style={INPUT} value={whId} onChange={(e) => setWh(e.target.value)}>
                    <option value="">Choose…</option>
                    {warehouses.map(([id, label]) => <option key={id} value={String(id)}>{label}</option>)}
                  </select>
                </Field>
                <Field label="Date dispatched"><input type="date" data-testid="dn-date" style={INPUT} value={date} onChange={(e) => setDate(e.target.value)} /></Field>
                <Field label="E-way bill no. (optional)"><input data-testid="dn-eway" style={INPUT} value={extra.eway} onChange={(e) => setExtra((x) => ({ ...x, eway: e.target.value }))} /></Field>
                <Field label="LR no. (optional)"><input data-testid="dn-lr" style={INPUT} value={extra.lr} onChange={(e) => setExtra((x) => ({ ...x, lr: e.target.value }))} /></Field>
                <Field label="Transporter (optional)"><input data-testid="dn-transporter" style={INPUT} value={extra.transporter} onChange={(e) => setExtra((x) => ({ ...x, transporter: e.target.value }))} /></Field>
                <Field label="Vehicle no. (optional)"><input data-testid="dn-vehicle" style={INPUT} value={extra.vehicle} onChange={(e) => setExtra((x) => ({ ...x, vehicle: e.target.value }))} /></Field>
              </div>
              {error && <div role="alert" data-testid="dn-error" style={ERR}>{error}</div>}
              <div style={{ display: "grid", justifyItems: "start", gap: spacing[2] }}>
                <button type="button" data-testid="dn-save" style={PRIMARY} disabled={busy} onClick={() => { void submit(); }}>
                  {busy ? "Creating…" : "Create delivery note"}
                </button>
                <span style={HELP}>Takes the next delivery note number{data.nextDn ? ` (${data.nextDn})` : ""}. Permanent: a mistake is put right by a reversing note. Nothing is emailed.</span>
              </div>
            </>
          )}
          {closed && <div style={NOTE}>The order is closed. An Admin can reopen it to dispatch more.</div>}
        </div>
      )}
      {data.notes.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["Delivery note", "Date", "From", "Lines", "E-way / LR", ""].map((h) => <th key={h} style={TH}>{h}</th>)}</tr></thead>
            <tbody>{data.notes.map((n) => (
              <tr key={n.id} data-testid={`dn-row-${n.id}`}>
                <td style={{ ...TD, fontWeight: typography.fontWeight.medium }}>{n.number}{n.reverses ? ` (reverses ${n.reverses})` : ""}{n.reversed_by ? ` · reversed by ${n.reversed_by}` : ""}</td>
                <td style={TD}>{formatDate(n.dn_date)}</td><td style={TD}>{n.warehouse ?? "—"}</td>
                <td style={TD}>{n.lines.map((l) => `${l.part_number ?? "?"} × ${fmt(l.qty)}`).join(", ")}</td>
                <td style={TD}>{[n.eway_bill_no, n.lr_no].filter(Boolean).join(" / ") || "—"}</td>
                <td style={{ ...TD, whiteSpace: "nowrap" }}>
                  <button type="button" data-testid={`dn-doc-${n.id}`} style={BTN} onClick={() => setPreview(n.id)}>Challan</button>{" "}
                  {canRecord && !n.reverses && !n.reversed_by && <button type="button" data-testid={`dn-reverse-${n.id}`} style={BTN} onClick={() => { void reverse(n); }}>Reverse</button>}
                </td>
              </tr>))}
            </tbody>
          </table>
        </div>
      )}
      {preview !== null && <DeliveryNotePreview id={preview} onClose={() => setPreview(null)} />}
    </section>
  );
}

// ---- CUSTOMS ACTUALS (import orders) ---------------------------------------------------

const CHARGE_FIELDS = [
  ["customs_rate", "Customs exchange rate (BoE)"], ["remittance_rate", "Remittance rate (paid at)"],
  ["oem_paid_inr", "INR paid to the OEM (bank advice)"], ["freight_inr", "Freight (INR)"],
  ["insurance_inr", "Insurance (INR)"], ["cha_inr", "CHA charges (INR)"], ["other_inr", "Other charges (INR)"],
] as const;
const LINE_FIELDS = [["assessable_value_inr", "Assessable value"], ["bcd_inr", "BCD"], ["sws_inr", "SWS"],
  ["igst_inr", "IGST (input credit)"]] as const;

function CustomsForm({ order, onDone }: { order: OemOrder; onDone: () => void }) {
  const [head, setHead] = useState<Record<string, string>>({});
  const [lines, setLines] = useState<Record<number, Record<string, string>>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const setL = (id: number, k: string, v: string) => setLines((x) => ({ ...x, [id]: { ...(x[id] ?? {}), [k]: v } }));
  const submit = async () => {
    setBusy(true); setError("");
    const res = await recordCustomsEntry(order.id, {
      boe_number: head.boe_number ?? "", boe_date: head.boe_date || null, port: head.port || undefined,
      other_label: head.other_label || undefined,
      ...Object.fromEntries(CHARGE_FIELDS.map(([k]) => [k, num(head[k] ?? "")])),
      lines: order.lines.filter((l) => (lines[l.id]?.qty ?? "") !== "").map((l) => ({
        order_line_id: l.id, qty: num(lines[l.id]?.qty ?? ""),
        ...Object.fromEntries(LINE_FIELDS.map(([k]) => [k, num(lines[l.id]?.[k] ?? "")])),
      })),
    });
    setBusy(false);
    if (!res.ok) { setError(res.error || "The Bill of Entry was not recorded."); return; }
    setHead({}); setLines({}); onDone();
  };
  return (
    <div data-testid={`customs-form-${order.id}`} style={{ padding: spacing[12], backgroundColor: semanticColors.panelBg, display: "grid", gap: spacing[8] }}>
      <div style={{ display: "grid", gap: spacing[8], gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
        <Field label="Bill of Entry no."><input data-testid={`customs-boe-${order.id}`} style={INPUT} value={head.boe_number ?? ""} onChange={(e) => setHead((h) => ({ ...h, boe_number: e.target.value }))} /></Field>
        <Field label="BoE date"><input type="date" data-testid={`customs-date-${order.id}`} style={INPUT} value={head.boe_date ?? ""} onChange={(e) => setHead((h) => ({ ...h, boe_date: e.target.value }))} /></Field>
        <Field label="Port"><input data-testid={`customs-port-${order.id}`} style={INPUT} value={head.port ?? ""} onChange={(e) => setHead((h) => ({ ...h, port: e.target.value }))} /></Field>
        {CHARGE_FIELDS.map(([k, label]) => (
          <Field key={k} label={label}><input data-testid={`customs-${k}-${order.id}`} inputMode="decimal" style={INPUT} placeholder="not recorded"
            value={head[k] ?? ""} onChange={(e) => setHead((h) => ({ ...h, [k]: e.target.value }))} /></Field>
        ))}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["Part", "Ordered", "Units on this BoE", ...LINE_FIELDS.map(([, l]) => `${l} (INR)`)].map((h) => <th key={h} style={TH}>{h}</th>)}</tr></thead>
          <tbody>{order.lines.map((l) => (
            <tr key={l.id}>
              <td style={TD}>{l.part_number}</td><td style={NUM}>{l.qty_ordered}</td>
              <td style={TD}><div style={{ display: "flex", gap: spacing[6], flexWrap: "wrap" }}>
                <input aria-label={`Units on this BoE, ${l.part_number}`} data-testid={`customs-qty-${l.id}`} inputMode="decimal" style={{ ...INPUT, width: 70 }}
                  value={lines[l.id]?.qty ?? ""} onChange={(e) => setL(l.id, "qty", e.target.value)} />
                <Remaining n={l.qty_ordered} testId={`customs-remaining-${l.id}`} onUse={(v) => setL(l.id, "qty", v)} />
              </div></td>
              {LINE_FIELDS.map(([k, label]) => (
                <td key={k} style={TD}><input aria-label={`${label}, ${l.part_number}`} data-testid={`customs-${k}-line-${l.id}`} inputMode="decimal"
                  style={{ ...INPUT, width: 100 }} placeholder="not recorded" value={lines[l.id]?.[k] ?? ""} onChange={(e) => setL(l.id, k, e.target.value)} /></td>
              ))}
            </tr>))}
          </tbody>
        </table>
      </div>
      <div style={HELP}>Leave a box empty when it is not known: it stays "not recorded", never 0. IGST is input credit and is never counted as a cost.</div>
      {error && <div role="alert" data-testid={`customs-error-${order.id}`} style={ERR}>{error}</div>}
      <div><button type="button" data-testid={`customs-save-${order.id}`} style={PRIMARY} disabled={busy} onClick={() => { void submit(); }}>
        {busy ? "Recording…" : "Record Bill of Entry"}</button></div>
    </div>
  );
}

export function CustomsBlock({ orders, data, canRecord }: { orders: OemOrder[]; data: FulfilmentData; canRecord: boolean }) {
  const imports = orders.filter((o) => o.domestic === false && o.status !== "cancelled");
  const [error, setError] = useState("");
  if (imports.length === 0) return null;
  const reverse = async (e: CustomsEntry) => {
    const reason = window.prompt(`Why is Bill of Entry ${e.boe_number} reversed? (required)`);
    if (reason === null) return;
    const res = await reverseCustomsEntry(e.id, reason);
    if (!res.ok) setError(res.error || "Not reversed."); else data.refresh();
  };
  const show = (v: number | null, money = true) => (v == null ? "Not recorded" : money ? formatRupees(v) : String(v));
  return (
    <section data-testid="customs" style={BOX}>
      <div style={HEAD}>
        <span style={TITLE}>Customs actuals (import orders)</span>
        <span style={NOTE}>What the Bill of Entry and the bank actually said. Used only by the Admin's "Actual" margin report -- never by pricing.</span>
      </div>
      {imports.map((o) => {
        const entries = data.customs.filter((e) => e.oem_order_id === o.id);
        return (
          <div key={o.id} data-testid={`customs-order-${o.id}`} style={{ borderBottom: `1px solid ${semanticColors.border}` }}>
            <div style={{ ...NOTE, padding: `${spacing[8]}px ${spacing[12]}px`, color: semanticColors.textPrimary }}>
              <strong>{o.number}</strong> · {o.oem} · {o.currency} · {entries.filter((e) => !e.reverses && !e.reversed).length} Bill(s) of Entry recorded
            </div>
            {entries.map((e) => (
              <div key={e.id} data-testid={`customs-entry-${e.id}`} style={{ ...NOTE, padding: `${spacing[4]}px ${spacing[12]}px` }}>
                {e.reverses ? <Pill tone="gray">Reversal</Pill> : e.reversed ? <Pill tone="gray">Reversed</Pill> : <Pill tone="green">BoE</Pill>}{" "}
                {e.boe_number} · {formatDate(e.boe_date)}{e.port ? ` · ${e.port}` : ""}
                {!e.reverses && <> · rate {show(e.customs_rate, false)} · remittance {show(e.remittance_rate, false)} · paid {show(e.oem_paid_inr)} · freight {show(e.freight_inr)} · insurance {show(e.insurance_inr)} · CHA {show(e.cha_inr)}{e.other_inr != null ? ` · ${e.other_label ?? "other"} ${formatRupees(e.other_inr)}` : ""}
                  {" · "}{e.lines.map((l) => `${l.part_number} × ${l.qty}: AV ${show(l.assessable_value_inr)}, BCD ${show(l.bcd_inr)}, SWS ${show(l.sws_inr)}, IGST ${show(l.igst_inr)}`).join("; ")}</>}
                {e.reverse_reason ? ` · reason: ${e.reverse_reason}` : ""}
                {canRecord && !e.reverses && !e.reversed && <> {" "}<button type="button" data-testid={`customs-reverse-${e.id}`} style={BTN} onClick={() => { void reverse(e); }}>Reverse</button></>}
              </div>
            ))}
            {canRecord && <CustomsForm key={`${o.id}-${data.tick}`} order={o} onDone={data.refresh} />}
          </div>
        );
      })}
      {error && <div role="alert" style={{ ...ERR, padding: spacing[12] }}>{error}</div>}
    </section>
  );
}

// ---- STEP 9: CLOSE OUT + THE "ACTUAL" REPORT (Admin) -------------------------------------

export function CloseOutBlock({ quoteId, data, isAdmin }: { quoteId: number; data: FulfilmentData; isAdmin: boolean }) {
  const closed = data.status?.closed ?? null;
  const [report, setReport] = useState<TrueMarginView | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (close: boolean) => {
    const reason = window.prompt(close ? "Why is this order closed? (required)" : "Why is it reopened? (required)");
    if (reason === null) return;
    setBusy(true);
    const res = await setQuoteOrderClosed(quoteId, close, reason);
    setBusy(false);
    if (!res.ok) { setError(res.error || "Not changed."); return; }
    setError(""); data.refresh();
  };
  const load = async () => {
    const res = await getTrueMargin(quoteId);
    if (!res.ok) { setError(res.error || "The Actual report could not be read."); return; }
    setError(""); setReport(res.view!);
  };
  return (
    <section data-testid="close-out" style={BOX}>
      <div style={HEAD}>
        <span style={TITLE}>Close out</span>
        {closed ? <Pill tone="green" testId="close-out-state">Closed</Pill> : <Pill tone="gray" testId="close-out-state">Open</Pill>}
        <span style={NOTE}>{closed ? `Closed by ${closed.closed_by ?? "—"}: ${closed.reason}` : "When everything is delivered (or the rest will not come), an Admin closes the order with a reason."}</span>
      </div>
      <div style={{ padding: spacing[12], display: "grid", gap: spacing[8] }}>
        {(data.status?.close_history ?? []).length > 0 && (
          <ol data-testid="close-out-history" style={{ margin: 0, paddingLeft: spacing[16], ...NOTE }}>
            {data.status!.close_history!.map((h, i) => <li key={i}>{h.kind === "closed" ? "Closed" : "Reopened"} by {h.by ?? "—"}{h.at ? ` on ${formatTimestampDate(h.at)}` : ""}: {h.reason}</li>)}
          </ol>
        )}
        {isAdmin ? (
          <div style={{ display: "flex", gap: spacing[8], flexWrap: "wrap", alignItems: "center" }}>
            <button type="button" data-testid={closed ? "close-out-reopen" : "close-out-close"} style={closed ? BTN : PRIMARY} disabled={busy}
              onClick={() => { void act(!closed); }}>{closed ? "Reopen order" : "Close order"}</button>
            <span style={HELP}>Records who and why; changes no figure, no quote and no number. It can be reversed by {closed ? "closing" : "reopening"} again.</span>
            <button type="button" data-testid="true-margin-load" style={BTN} onClick={() => { void load(); }}>Show the Actual margin</button>
          </div>
        ) : (
          <div style={NOTE}>Closing and the Actual margin report are for an Admin.</div>
        )}
        {error && <div role="alert" style={ERR}>{error}</div>}
        {report && <TrueMarginTable view={report} />}
      </div>
    </section>
  );
}

function TrueMarginTable({ view }: { view: TrueMarginView }) {
  return (
    <div data-testid="true-margin" style={{ display: "grid", gap: spacing[6] }}>
      <div style={{ ...NOTE, color: semanticColors.textPrimary }}>
        <strong>Actual</strong> -- read-only, from what was recorded (receipts, deliveries, customs, payments). Never stored, never used for pricing.
        Revenue = quoted unit sell price × quantity actually delivered. IGST is input credit and is not a cost.
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["#", "Part", "Delivered", "Short-closed", "Revenue", "Cost", "Cost basis", "Margin", "Margin %", "Quoted value", "IGST credit", "Missing"].map((h) => <th key={h} style={TH}>{h}</th>)}</tr></thead>
          <tbody>{view.lines.map((l) => (
            <tr key={l.line_id} data-testid={`tm-line-${l.line_no}`}>
              <td style={TD}>{l.line_no}</td><td style={TD}>{l.part_number}</td>
              <td style={NUM}>{fmt(l.delivered)} of {fmt(l.qty)}</td><td style={NUM}>{l.short_closed ? fmt(l.short_closed) : "—"}</td>
              <td style={NUM}>{l.revenue == null ? "Not computed" : formatRupees(l.revenue)}</td>
              <td style={NUM}>{l.cost == null ? "Not computed" : formatRupees(l.cost)}</td>
              <td style={TD} data-testid={`tm-basis-${l.line_no}`}
                title={(l.cost_basis_detail ?? []).map((b) => `${b.order}${b.boe ? `, BoE ${b.boe}` : ""}: ${b.basis}`).join("; ") || undefined}>
                {(l.cost_basis ?? ["Not recorded"]).join(" + ")}
              </td>
              <td style={NUM}>{l.margin == null ? "Not computed" : formatRupees(l.margin)}</td>
              <td style={NUM}>{l.margin_pct == null ? "—" : `${l.margin_pct.toFixed(2)}%`}</td>
              <td style={NUM}>{formatRupees(l.quoted_revenue)}</td><td style={NUM}>{formatRupees(l.igst_credit)}</td>
              <td style={{ ...TD, color: l.missing.length ? colors.amber800 : undefined }}>{l.missing.length ? l.missing.join(" ") : "—"}</td>
            </tr>))}
          </tbody>
        </table>
      </div>
      <div data-testid="true-margin-total" style={{ ...NOTE, color: semanticColors.textPrimary }}>
        {view.totals ? `Total: revenue ${formatRupees(view.totals.revenue)}, cost ${formatRupees(view.totals.cost)}, margin ${formatRupees(view.totals.margin)}${view.totals.margin_pct != null ? ` (${view.totals.margin_pct.toFixed(2)}%)` : ""}.`
          : "No total until every line can be computed -- see what is missing above."}
      </div>
    </div>
  );
}
